import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { translations as staticTranslations } from '../translations/translations';

const LanguageContext = createContext();

// API URL - change this to your production URL when deploying
const API_URL = process.env.REACT_APP_TRANSLATION_API || 'https://translation-ipa.avopla.ca/api';

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [translations, setTranslations] = useState(staticTranslations);
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState({});

  // Deep merge function to properly merge nested objects
  const deepMerge = (target, source) => {
    const result = { ...target };
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = deepMerge(target[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
    return result;
  };

  // Fetch translations from API
  const fetchTranslations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/translations/formatted`);
      if (response.ok) {
        const data = await response.json();
        // Deep merge with static translations (API takes precedence)
        setTranslations({
          en: deepMerge(staticTranslations.en, data.en || {}),
          fr: deepMerge(staticTranslations.fr, data.fr || {})
        });
        console.log('Translations loaded from API');
      }
    } catch (error) {
      console.log('Using static translations (API not available):', error.message);
      // Keep using static translations
    }
  }, []);

  // Fetch images from API
  const fetchImages = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/images/formatted`);
      if (response.ok) {
        const data = await response.json();
        console.log('Images loaded from API:', data);
        setImages(data);
      }
    } catch (error) {
      console.log('Images API not available:', error.message);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      
      // Detect browser language on mount
      const browserLang = navigator.language || navigator.userLanguage;
      const detectedLang = browserLang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
      
      // Check if language is saved in localStorage
      const savedLang = localStorage.getItem('preferredLanguage');
      
      if (savedLang) {
        setLanguage(savedLang);
      } else {
        setLanguage(detectedLang);
        localStorage.setItem('preferredLanguage', detectedLang);
      }

      // Fetch translations and images from API
      await Promise.all([fetchTranslations(), fetchImages()]);
      
      setLoading(false);
    };

    init();
  }, [fetchTranslations, fetchImages]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('preferredLanguage', lang);
  };

  // Helper to get translation with fallback
  const t = (section) => {
    return translations[language]?.[section] || staticTranslations[language]?.[section] || {};
  };

  // Helper to get image URL and alt text
  const getImage = (name) => {
    console.log('Getting image:', name, 'Available images:', Object.keys(images));
    if (images[name]) {
      return {
        url: images[name].url,
        alt: language === 'en' ? images[name].altEn : images[name].altFr
      };
    }
    return null;
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      changeLanguage, 
      translations, 
      loading,
      images,
      getImage,
      t
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
